// src/components/ProjectManagement/KanbanBoard.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DndContext, closestCorners, DragOverlay } from '@dnd-kit/core';
import { db } from '../../firebase/config';
import { collection, query, where, onSnapshot, updateDoc, doc, addDoc, getDoc, getDocs } from 'firebase/firestore';
import KanbanColumn from './KanbanColumn';
import WorkerSidebar from './WorkerSidebar';
import TaskAssignmentModal from './TaskAssignmentModal';
import KanbanTask from './KanbanTask';
import AddTaskModal from './AddTaskModal';

// Global cache - persists across component unmounts
let globalProgressCache = {};
let globalTasksCache = { pending: [], ongoing: [], completed: [], overdue: [] };
let isFetchingProgress = false;
let progressFetchPromise = null;

// CRITICAL: Track if cache is valid
let cacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const showToast = (message, type = 'success') => {
  const toast = document.createElement('div');
  toast.textContent = message;
  toast.style.position = 'fixed';
  toast.style.bottom = '20px';
  toast.style.right = '20px';
  toast.style.backgroundColor = type === 'error' ? '#dc2626' : '#10b981';
  toast.style.color = 'white';
  toast.style.padding = '12px 24px';
  toast.style.borderRadius = '12px';
  toast.style.zIndex = '9999';
  toast.style.fontWeight = '600';
  toast.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
  
  document.body.appendChild(toast);
  setTimeout(() => toast.remove(), 3000);
};

const KanbanBoard = ({ projectId, currentUser, userRole, onFilterByProject, filterProjectId, clearFilter, filterProjectName: propFilterProjectName }) => {
  // Initialize with cached data if available AND not expired
  const [tasks, setTasks] = useState(() => {
    const now = Date.now();
    if (globalTasksCache.pending.length > 0 && (now - cacheTimestamp) < CACHE_TTL) {
      return globalTasksCache;
    }
    return { pending: [], ongoing: [], completed: [], overdue: [] };
  });
  
  const [activeTask, setActiveTask] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [loading, setLoading] = useState(true);
  const [projectName, setProjectName] = useState('');
  const [projectProgressMap, setProjectProgressMap] = useState(() => globalProgressCache);
  const [filterName, setFilterName] = useState(propFilterProjectName || '');
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    setFilterName(propFilterProjectName || '');
  }, [propFilterProjectName]);

  // Fetch project name (1 read)
  useEffect(() => {
    if (!projectId || projectId === 'all') {
      setProjectName('All Projects');
      return;
    }
    const fetchProjectName = async () => {
      try {
        const projectDoc = await getDoc(doc(db, 'projects', projectId));
        if (projectDoc.exists()) {
          setProjectName(projectDoc.data().projectName);
        }
      } catch (error) {
        console.error('Error fetching project name:', error);
      }
    };
    fetchProjectName();
  }, [projectId]);

  // OPTIMIZED: Fetch all projects progress ONCE and cache it
  useEffect(() => {
    if (Object.keys(globalProgressCache).length > 0) {
      setProjectProgressMap(globalProgressCache);
      return;
    }
    
    if (isFetchingProgress) {
      if (progressFetchPromise) {
        progressFetchPromise.then(progressMap => {
          setProjectProgressMap(progressMap);
          globalProgressCache = progressMap;
        });
      }
      return;
    }
    
    isFetchingProgress = true;
    
    progressFetchPromise = (async () => {
      try {
        const projectsSnapshot = await getDocs(collection(db, 'projects'));
        const projects = projectsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        const projectIds = projects.map(p => p.id);
        
        if (projectIds.length === 0) return {};
        
        const tasksPromises = projectIds.map(pid => 
          getDocs(query(collection(db, 'tasks'), where('projectId', '==', pid)))
        );
        
        const allTasksSnapshots = await Promise.all(tasksPromises);
        
        const progressMap = {};
        projects.forEach((project, index) => {
          const tasks = allTasksSnapshots[index].docs.map(d => d.data());
          const completedTasks = tasks.filter(t => t.status === 'completed' && t.qcVerified === true).length;
          const progress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;
          progressMap[project.id] = progress;
        });
        
        globalProgressCache = progressMap;
        return progressMap;
      } catch (error) {
        console.error('Error fetching progress:', error);
        return {};
      } finally {
        isFetchingProgress = false;
        progressFetchPromise = null;
      }
    })();
    
    progressFetchPromise.then(progressMap => {
      setProjectProgressMap(progressMap);
    });
  }, []);

  // Tasks query with REAL-TIME cache updates
  useEffect(() => {
    let isMounted = true;
    setLoading(true);
    
    let q;
    if (filterProjectId) {
      q = query(collection(db, 'tasks'), where('projectId', '==', filterProjectId));
    } else if (projectId && projectId !== 'all') {
      q = query(collection(db, 'tasks'), where('projectId', '==', projectId));
    } else {
      q = query(collection(db, 'tasks'));
    }
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!isMounted) return;
      
      const tasksData = { pending: [], ongoing: [], completed: [], overdue: [] };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      snapshot.forEach(doc => {
        const task = { id: doc.id, ...doc.data() };
        let endDate = null;
        if (task.endDate) {
          if (task.endDate.toDate) {
            endDate = task.endDate.toDate();
          } else {
            endDate = new Date(task.endDate);
          }
          endDate.setHours(0, 0, 0, 0);
        }
        
        // FIXED: Only PENDING tasks can be overdue. ONGOING tasks NEVER go to overdue.
        const isOverdue = (task.status === 'pending') && endDate && endDate < today;
        
        if (isOverdue) {
          tasksData.overdue.push(task);
        } else if (task.status === 'pending') {
          tasksData.pending.push(task);
        } else if (task.status === 'ongoing') {
          tasksData.ongoing.push(task);
        } else if (task.status === 'completed') {
          tasksData.completed.push(task);
        }
      });
      
      tasksData.pending.sort((a, b) => (a.order || 0) - (b.order || 0));
      tasksData.ongoing.sort((a, b) => (a.order || 0) - (b.order || 0));
      tasksData.completed.sort((a, b) => (a.order || 0) - (b.order || 0));
      tasksData.overdue.sort((a, b) => {
        const dateA = a.endDate?.toDate?.() || new Date(a.endDate);
        const dateB = b.endDate?.toDate?.() || new Date(b.endDate);
        return dateA - dateB;
      });
      
      globalTasksCache = tasksData;
      cacheTimestamp = Date.now();
      
      setTasks(tasksData);
      setLoading(false);
      setInitialLoadComplete(true);
    }, (error) => {
      console.error('Error fetching tasks:', error);
      if (isMounted) {
        showToast('Error loading tasks. Please refresh.', 'error');
        setLoading(false);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [projectId, filterProjectId, refreshKey]);

  const canEdit = userRole === 'manager' || userRole === 'ceo' || userRole === 'quality-control' || userRole === 'managing-director';

  const handleDragStart = (event) => {
    const { active } = event;
    const allTasks = [...tasks.pending, ...tasks.ongoing, ...tasks.completed, ...tasks.overdue];
    const task = allTasks.find(t => t.id === active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    
    if (!over) return;

    if (over.id.toString().startsWith('worker-')) {
      const workerId = over.id.toString().replace('worker-', '');
      const workerDoc = await getDoc(doc(db, 'users', workerId));
      const worker = { id: workerId, ...workerDoc.data() };
      const allTasks = [...tasks.pending, ...tasks.ongoing, ...tasks.completed, ...tasks.overdue];
      const task = allTasks.find(t => t.id === active.id);
      
      if (task && worker) {
        setSelectedTask(task);
        setSelectedWorker(worker);
        setShowAssignmentModal(true);
      }
      return;
    }

    const newStatus = over.id;
    const taskId = active.id;
    
    try {
      const updateData = { 
        status: newStatus === 'overdue' ? 'pending' : newStatus, 
        updatedAt: new Date() 
      };
      
      if (newStatus === 'completed') {
        updateData.qcVerified = false;
        updateData.completedAt = new Date();
      }
      
      if (newStatus !== 'completed') {
        updateData.qcVerified = false;
        updateData.qcVerifiedBy = null;
        updateData.qcVerifiedAt = null;
      }
      
      await updateDoc(doc(db, 'tasks', taskId), updateData);
      showToast(`Task moved to ${newStatus}`, 'success');
      setRefreshKey(prev => prev + 1);
      
      globalProgressCache = {};
      
    } catch (error) {
      console.error('Error moving task:', error);
      showToast('Failed to move task', 'error');
    }
  };

  const handleTaskAssign = async (result) => {
    if (result?.success) {
      showToast(`Task assigned successfully`, 'success');
      setShowAssignmentModal(false);
      setRefreshKey(prev => prev + 1);
      globalProgressCache = {};
    }
    return result;
  };

  const handleAddTask = async (taskData) => {
    if (!projectId || projectId === 'all') {
      showToast('Please select a specific project to add tasks', 'error');
      return;
    }
    
    try {
      const allTasks = [...tasks.pending, ...tasks.ongoing, ...tasks.completed, ...tasks.overdue];
      const maxOrder = Math.max(...allTasks.map(t => t.order || 0), 0);
      
      const startDate = new Date(taskData.startDate);
      const endDate = new Date(taskData.endDate);
      const estimatedDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
      
      const newTask = {
        projectId: projectId,
        projectName: projectName,
        title: taskData.title,
        description: taskData.description || '',
        category: taskData.category || 'General',
        estimatedDays: estimatedDays,
        startDate: startDate,
        endDate: endDate,
        priority: taskData.priority || 'normal',
        status: 'pending',
        order: maxOrder + 1,
        assignedTo: null,
        assignedToName: null,
        assignedBy: null,
        assignedAt: null,
        deadline: endDate,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      await addDoc(collection(db, 'tasks'), newTask);
      showToast('✅ New task added successfully!', 'success');
      setShowAddTaskModal(false);
      setRefreshKey(prev => prev + 1);
      globalProgressCache = {};
      
    } catch (error) {
      console.error('Error adding task:', error);
      showToast('Failed to add task', 'error');
    }
  };

  const handleFilterByProject = (clickedProjectId, clickedProjectName) => {
    if (onFilterByProject) {
      onFilterByProject(clickedProjectId, clickedProjectName);
    }
  };

  // Show loading only on first ever load
  if (loading && !initialLoadComplete && tasks.pending.length === 0 && tasks.ongoing.length === 0) {
    return (
      <div className="kanban-loading-container">
        <div className="skeleton-kanban-header"></div>
        <div className="skeleton-kanban-columns">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="skeleton-column"></div>
          ))}
        </div>
      </div>
    );
  }

  const isFiltered = !!filterProjectId;
  const displayFilterName = filterName || propFilterProjectName;

  return (
    <>
      <div className="kanban-header">
        <div className="kanban-title">
          <h2>
            📋 
            {isFiltered ? ` Filtered: ${displayFilterName || 'Project'}` : (projectName || 'All Projects')}
          </h2>
          {canEdit && !isFiltered && projectId && projectId !== 'all' && (
            <button className="add-task-btn-header" onClick={() => setShowAddTaskModal(true)}>
              + Add New Task
            </button>
          )}
          {isFiltered && clearFilter && (
            <button className="clear-filter-btn" onClick={clearFilter}>
              ✕ Clear Filter
            </button>
          )}
        </div>
      </div>

      <DndContext 
        onDragStart={handleDragStart} 
        onDragEnd={handleDragEnd} 
        collisionDetection={closestCorners}
      >
        <div className="kanban-layout">
          <div className="kanban-columns">
            <KanbanColumn 
              id="overdue" 
              title="Overdue" 
              tasks={tasks.overdue} 
              color="red"
              canEdit={canEdit}
              currentUser={currentUser}
              onTaskUpdate={() => setRefreshKey(prev => prev + 1)}
              onFilterByProject={handleFilterByProject}
              projectProgressMap={projectProgressMap}
            />
            <KanbanColumn 
              id="pending" 
              title="Pending" 
              tasks={tasks.pending} 
              color="blue"
              canEdit={canEdit}
              currentUser={currentUser}
              onTaskUpdate={() => setRefreshKey(prev => prev + 1)}
              onFilterByProject={handleFilterByProject}
              projectProgressMap={projectProgressMap}
            />
            <KanbanColumn 
              id="ongoing" 
              title="Ongoing" 
              tasks={tasks.ongoing} 
              color="orange"
              canEdit={canEdit}
              currentUser={currentUser}
              onTaskUpdate={() => setRefreshKey(prev => prev + 1)}
              onFilterByProject={handleFilterByProject}
              projectProgressMap={projectProgressMap}
            />
            <KanbanColumn 
              id="completed" 
              title="Completed" 
              tasks={tasks.completed} 
              color="green"
              canEdit={canEdit}
              currentUser={currentUser}
              onTaskUpdate={() => setRefreshKey(prev => prev + 1)}
              onFilterByProject={handleFilterByProject}
              projectProgressMap={projectProgressMap}
            />
          </div>
          
          {canEdit && <WorkerSidebar />}
        </div>
        
        <DragOverlay>
          {activeTask && <KanbanTask task={activeTask} canEdit={canEdit} currentUser={currentUser} />}
        </DragOverlay>
      </DndContext>
      
      {showAssignmentModal && (
        <TaskAssignmentModal
          task={selectedTask}
          worker={selectedWorker}
          currentUser={currentUser}
          projectId={selectedTask?.projectId}
          onClose={() => setShowAssignmentModal(false)}
          onAssign={handleTaskAssign}
        />
      )}

      {showAddTaskModal && (
        <AddTaskModal
          onClose={() => setShowAddTaskModal(false)}
          onAdd={handleAddTask}
        />
      )}
    </>
  );
};

export default KanbanBoard;